import { API_BASE_URL } from "../../constant/constant";
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { Container, Content, Header, Heading } from "./Style";
import { Flex, Button, Typography, message as antMessage } from "antd";
import {
  DeleteOutlined,
  PlusOutlined,
  CopyOutlined,
  MessageOutlined,
  FileOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import ChatInput from "./ChatInput";
import { ChatDiv, ChatBox, ChatText, ScrollableChat } from "../Summarize/Style";
import styled from "styled-components";
import BackIcon from "../../assets/back-icon.svg";
import Star from "../../assets/star.svg";
import UserIcon from "../../assets/user.png"
import axios from "axios";
import { handleFileUploadWithSignedURL } from "../Environment/Asconfig";
import ReactMarkdown from "react-markdown";
import { MessageContainer } from "../Avatar/style";
import CircularProgress from "@mui/material/CircularProgress";
import { toast } from "react-toastify";
import { useSnackbar } from "../../context/SnackBarContext";
// Move styled components to top level
const ChatContainer = styled.div`
  height: calc(100vh - 160px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  padding: 20px;
`;

const MessageActions = styled.div`
  display: none;
  position: absolute;
  right: 10px;
  top: 10px;
  gap: 10px;
  padding: 4px;
  background-color: white;
`;

const ErrorMessage = styled(ChatDiv)`
  color: #ff4d4f;
  background: #fff2f0;
  border: 1px solid #ffccc7;
  padding: 8px 12px;
  border-radius: 8px;
`;

const ChatBoxWrapper = styled(ChatBox)`
  position: relative;
  &:hover ${MessageActions} {
    display: flex;
  }
`;

const LoadingMessage = styled(ChatBoxWrapper)`
  @keyframes pulse {
    0% {
      opacity: 0.6;
    }
    50% {
      opacity: 1;
    }
    100% {
      opacity: 0.6;
    }
  }
  animation: pulse 1.5s infinite;
  background: #f5f5f5;
`;
const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  margin-bottom: 120px;
  scroll-behavior: smooth;
`;

const InputWrapper = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 0 20px;
  background: white;
`;

const UserMessageWrapper = styled(ChatBoxWrapper)`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 10px;
  gap: 8px;
  border-radius: 8px;
  border: 1px solid rgba(49, 49, 51, 0.06);
  background: rgba(255, 255, 255, 1);
  // max-width: 50%;
`;

const AssistantMessageWrapper = styled(ChatBoxWrapper)`
  max-width: 80%;
  padding: 10px;
  border-radius: 8px;
  border: 1px solid rgba(49, 49, 51, 0.06);
  background: linear-gradient(
    180deg,
    rgba(49, 49, 51, 0) 0%,
    rgba(49, 49, 51, 0.12) 100%
  );

  box-shadow: 0px 30px 80px 0px rgba(220, 220, 220, 0.2);
`;

const UserAvatar = styled.img`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  margin-right: 8px;
`;

export default function RunSkills() {
  const { isSidebarOpen } = useOutletContext();
  const navigate = useNavigate();
  const { id } = useParams();
  const [chatId, setChatId] = useState(null);
  const [message, setMessage] = useState("");
  const [file, setFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [skillData, setSkillData] = useState(null);
  const [clearLoading, setClearLoading] = useState(false);
  const token = localStorage.getItem("token");
  const showSnackbar = useSnackbar();

  const username = localStorage.getItem("name");

  // Add ref for auto-scrolling
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const initializeChat = async () => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/skill/chat/${id}`,
          {
            headers: {
              "Content-Type": "application/json",
              "x-auth-token": token || "",
            },
          }
        );

        if (response.data.responseCode === 429){
          showSnackbar(response.data.message, "error");
          return;
        }
        setChatId(response.data.data._id);

        setMessages(response.data.data.messages);
      } catch (error) {
        // Create new chat when none exists (404 error)
        const newChat = await axios.post(
          `${API_BASE_URL}/api/skill/chat/`,
          { skillId: id },
          {
            headers: {
              "Content-Type": "application/json",
              "x-auth-token": token || "",
            },
          }
        );

        if (newChat.data.responseCode === 429){
          showSnackbar(newChat.data.message, "error");
          return;
        }
        setChatId(newChat.data.data._id);
      }
    };

    initializeChat();

    const fetchSkillData = async () => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/skill/${id}`,
          {
            headers: {
              "Content-Type": "application/json",
              "x-auth-token": token || "",
            },
          }
        );

        if (response.data.responseCode === 429){
          showSnackbar(response.data.message, "error");
          return;
        }
        setSkillData(response.data.data);
      } catch (error) {
        console.error("Error fetching skill data", error);
      }
    };

    fetchSkillData();
  }, [id]);

  const handleClearChat = async () => {
    try {
      setClearLoading(true);
      await axios.post(`${API_BASE_URL}/api/skill/chat/delete/${id}`,
      {
        headers: {
          "Content-Type": "application/json",
          "x-auth-token": token || "",
        },
      });

      if (response.data.responseCode === 429){
        showSnackbar(response.data.message, "error");
        return;
      }
      setMessages([]);
      antMessage.success('Chat cleared successfully');
    } catch (error) {
      console.error('Error clearing chat:', error);
      antMessage.error('Failed to clear chat');
    } finally {
      setClearLoading(false);
    }
  };

  const saveMessage = async (role, content, fileUrls = []) => {
    try {
      console.log(chatId);
      await axios.post(`${API_BASE_URL}/api/skill/chat/message`, {
        chatId,
        role,
        message: content,
        fileUrls: fileUrls
      },
      {
        headers: {
          "Content-Type": "application/json",
          "x-auth-token": token || "",
        },
      });

      if (response.data.responseCode === 429){
        showSnackbar(response.data.message, "error");
        return;
      }
    } catch (error) {
      console.error("Error saving message:", error);
    }
  };

  const uploadFileToS3 = async (file) => {
    try {
      const response = await handleFileUploadWithSignedURL(file);
      return response.fileUrl;
    } catch (error) {
      toast.error(error);
      return null
    }
  };

  const handleRunSkill = async () => {
    if (!message.trim() && !file) return;

    try {
      setIsLoading(true);

      const currentMessage = message.trim();
      const currentFile = file;
      // Reset states
      setFile(null);
      setMessage("");

      // Use default message if only file is present
      const finalMessage = currentMessage.trim() || "Here is the file";

      // Get file names before uploading
      const fileNames = currentFile ? [currentFile.name] : [];

      // Save user message with file names
      await saveMessage("user", currentMessage, fileNames);
      setMessages((prev) => [...prev, {
        role: "user",
        message: currentMessage,
        fileUrls: fileNames
      }]);

      // Upload files if present
      let uploadedUrls = [];
      if (currentFile) {
        const fileUrl = await uploadFileToS3(currentFile);
        if (!fileUrl) throw new Error("File upload failed");
        uploadedUrls.push(fileUrl);
      }

      const payload = {
        user_id: localStorage.getItem("id"),
        skill_id: id,
        query: finalMessage,
        user_name: username,
        objectives: skillData.objectives,
        task_guidelines: skillData.taskGuidelines,
        required_output: skillData.requiredOutput,
        messages: messages,
        uploaded_urls: uploadedUrls,
        other_rules: skillData.otherRules,
      };

      const response = await axios.post(
        "https://flowproai.aivoria.nl/ai_flow_pro/run_skill",
        payload
      );

      // Check if processing has started
      if (response?.data?.message === "Processing Started" && response?.data?.id) {
        // Poll for results
        let skillResponse = { status: "incomplete" };

        // Function to poll for results
        const pollForResponse = async () => {
          try {
            const pollResponse = await axios.post(
              "https://flowproai.aivoria.nl/ai_flow_pro/get_skill_response",
              { id: response?.data?.id }
            );
            return pollResponse?.data;
          } catch (error) {
            console.error("Error polling for response:", error);
            return { status: "error", content: "Error fetching response" };
          }
        };

        // Poll until we get a complete status or encounter an error
        while (skillResponse?.status === "incomplete") {
          skillResponse = await pollForResponse();

          // If still incomplete, wait for 3 seconds before polling again
          if (skillResponse?.status === "incomplete") {
            await new Promise(resolve => setTimeout(resolve, 3000));
          }
        }

        // Process the final result
        if (skillResponse?.status === "complete") {
          // Save assistant message
          await saveMessage("assistant", skillResponse?.content);
          setMessages((prev) => [
            ...prev,
            { role: "assistant", message: skillResponse?.content },
          ]);
        } else if (skillResponse?.status === "error") {
          // Save error message
          await saveMessage("assistant", "Error generating response");
          setMessages((prev) => [
            ...prev,
            { role: "assistant", message: "Error generating response" },
          ]);
        }
      } else {
        // Handle the old response format
        await saveMessage("assistant", response?.data?.error);
        setMessages((prev) => [
          ...prev,
          { role: "assistant", message: response?.data?.error },
        ]);
      }
    } catch (error) {
      console.error("Error running skill:", error);
      antMessage.error("Failed to process request");

      // Add error message to chat
      await saveMessage("assistant", "Error generating response");
      setMessages((prev) => [
        ...prev,
        { role: "assistant", message: "Error generating response" },
      ]);
    } finally {
      setIsLoading(false);
    }
  };


  const handleCopy = (content) => {
    navigator.clipboard.writeText(content);
    antMessage.success("Copied to clipboard");
  };

  const handleFeedback = (messageId) => {
    // Implement feedback functionality
  };

  const handleFileChange = (info) => {
    setFile(info.file);
  };

  const handleRemoveFile = () => {
    setFile(null);
  };

  const ChatMessage = ({ message }) => (
    <ChatDiv>
      {message.role === "user" ? (
        <div style={{ display: "flex", flexDirection: "column", maxWidth: "50%" }}>
          {message.message && (
            <UserMessageWrapper>
              <UserAvatar
                src={UserIcon}
                alt="User"
              />
              <ChatText>{message.message}</ChatText>
            </UserMessageWrapper>
          )}
          {message.fileUrls && message.fileUrls.length > 0 && message.fileUrls.map((fileName, index) => (
            <Flex
              key={index}
              align="center"
              style={{
                padding: "8px",
                borderRadius: "8px",
                backgroundColor: "#F5F5F5",
              }}
            >
              <FileOutlined style={{ fontSize: "18px", color: "#595959" }} />
              <Typography.Text style={{ marginLeft: "8px", flex: 1 }}>
                {fileName}
              </Typography.Text>
            </Flex>
          ))}
        </div>
      ) : (
        <AssistantMessageWrapper>
          <ChatText>
            <ReactMarkdown>{message.message}</ReactMarkdown>
          </ChatText>
          <MessageActions>
            <Button
              icon={<CopyOutlined />}
              type="text"
              onClick={() => handleCopy(message.message)}
            />
            {/* <Button
              icon={<MessageOutlined />}
              type="text"
              onClick={() => handleFeedback(message._id)}
            /> */}
          </MessageActions>
        </AssistantMessageWrapper>
      )}
    </ChatDiv>
  );

  return (
    <Container isOpen={isSidebarOpen}>
      <Flex justify="space-between" align="center">
        <Flex gap={15} align="center">
          <Header onClick={() => navigate("/skills")}>
            <img src={BackIcon} alt="back" />
            <Heading>Run skill</Heading>
          </Header>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate("/add-skills")}
            style={{ marginTop: "1.8rem" }}
          >
            New Skill
          </Button>
        </Flex>
        <Button
          type="default"
          icon={clearLoading ? <CircularProgress size={14} /> : <DeleteOutlined />}
          onClick={handleClearChat}
          loading={clearLoading}
          style={{ marginTop: "1.8rem", background: "#F1F1F5" }}
        >
          {clearLoading ? 'Clearing...' : 'Clear Chat'}
        </Button>
      </Flex>
      <Content>
        <div style={{ marginLeft: "auto", marginRight: "auto", width: "70%" }}>
          <ChatContainer>
            {messages.length <= 1 && !isLoading ? (
              <Flex justify="center" align="center" style={{ height: "100%" }}>
                <Flex vertical align="center" gap={50} style={{ width: "50%" }}>
                  <Flex
                    gap={10}
                    justify="center"
                    align="center"
                    style={{ width: "100%" }}
                  >
                    <img
                      src={Star}
                      alt="logo"
                      width={32}
                      height={32}
                      style={{ marginTop: "0.5rem" }}
                    />
                    <Typography.Title level={1} color="#313133">
                      Good afternoon {username}
                    </Typography.Title>
                  </Flex>
                </Flex>
              </Flex>
            ) : (
              <MessagesContainer>
                {messages.map((msg, index) => (
                  <ChatMessage key={index} message={msg} />
                ))}
                {isLoading && (
                  <LoadingMessage style={{ marginTop: "1rem" }}>
                    <ChatText>🤖 Assistant is thinking...</ChatText>
                  </LoadingMessage>
                )}

                <div ref={messagesEndRef} />
              </MessagesContainer>)}

            <InputWrapper>
              <ChatInput
                message={message}
                setMessage={setMessage}
                handleSend={handleRunSkill}
                file={file}
                handleFileChange={handleFileChange}
                handleRemoveFile={handleRemoveFile}
              />
            </InputWrapper>
          </ChatContainer>
        </div>
      </Content>
    </Container>
  );
}
